import { notIncludes } from '@csp/csp-common-util';
import { GridProps, styled, useTheme } from '@mui/material';
import { PaletteProps, SpacingProps, compose, css, palette, spacing } from '@mui/system';
import { FC, PropsWithChildren } from 'react';
import { Maybe } from '@csp/csp-common-model';
import { Link } from 'react-router-dom';
import { BorderColor } from '../../../../common/model/BorderColor';
import { Grid } from '../../../layout/grid/Grid';
import { ListRowMapper } from '../mapper/ListRowMapper';

type ForwardProps = {
  highlightColor?: BorderColor;
  border?: BorderColor;
  disabled?: boolean;
  outlined?: boolean;
  noShadow?: boolean;
};

type LinkProps = {
  linkTo: Maybe<string>;
  navBackPath?: Maybe<string>;
  onClick?: never;
};
type NonLinkProps = {
  linkTo?: never;
  navBackPath?: never;
};

type BasicListRowProps = GridProps & PaletteProps & SpacingProps & ForwardProps;
type ListRowProps = (Omit<BasicListRowProps, 'onClick'> & LinkProps) | (BasicListRowProps & NonLinkProps);
type ListNavigationProps = LinkProps & PropsWithChildren;

const ListRowState = styled(Grid, {
  shouldForwardProp: notIncludes(['disabled', 'highlightColor', 'noShadow', 'outlined']),
})<BasicListRowProps>`
  ${compose(palette, spacing)}
  ${({ theme, outlined, disabled, highlightColor, noShadow, border, onClick }): string => `
    outline: ${ListRowMapper.toOutline(theme, outlined, border)};
    box-shadow: ${ListRowMapper.toBoxShadow(theme, noShadow, outlined)};
    border-radius: 5px;
    border-left: 5px solid ${ListRowMapper.toBorderLeftColor(theme, highlightColor)};

    &:hover {
      cursor: ${disabled || !onClick ? 'auto' : 'pointer'};
      box-shadow: ${disabled || !onClick ? 'auto' : theme.shadows[2]};
    }
  `}
`;

const StyledLink = styled(Link)(({ theme }) => {
  const hoverStyles = css`
    cursor: pointer !important;
    box-shadow: ${theme.shadows[2]};
  `;

  return css`
    all: unset;

    > *:hover {
      ${hoverStyles}
    }

    &:hover,
    &:focus-visible {
      > * {
        ${hoverStyles}
      }
    }
  `;
});

const ListRowWrapper: FC<ListNavigationProps> = ({ children, linkTo, navBackPath, ...props }) =>
  linkTo ? (
    <StyledLink
      to={linkTo}
      state={
        navBackPath && {
          navBackPath,
        }
      }
      {...props}
    >
      {children}
    </StyledLink>
  ) : (
    children
  );

export const ListRow: FC<ListRowProps> = ({
  highlightColor,
  disabled,
  outlined,
  children,
  noShadow = false,
  border,
  linkTo,
  navBackPath,
  onClick,
  ...props
}) => {
  const { palette } = useTheme();

  return (
    <ListRowWrapper linkTo={disabled ? undefined : linkTo} navBackPath={navBackPath}>
      <ListRowState
        mt={1.25}
        onClick={disabled ? undefined : onClick}
        bgcolor={outlined ? palette.grey[50] : palette.common.white}
        outlined={outlined}
        highlightColor={highlightColor}
        border={border}
        disabled={disabled}
        noShadow={noShadow}
        {...props}
      >
        {children}
      </ListRowState>
    </ListRowWrapper>
  );
};
